import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FastScanInput from "@/components/FastScanInput";
import { DEV_STATUS, DEV_ORDER, DESTINOS, fmtDate } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, Loader2, Plus, Minus, Trash2, Undo2, Search, Truck, PackageCheck, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const CD_ROLES = ["ADMIN", "SUPER_ADMIN", "MASTER", "GESTOR", "SUPERVISOR", "ESTOQUISTA"];

function DevBadge({ status }) {
  const pending = { FINALIZANDO: "Finalização pendente", CANCELANDO: "Cancelamento pendente" };
  const s = DEV_STATUS[status] || (pending[status] ? { label: pending[status], cls: "text-amber-300 bg-amber-950 border-amber-600" } : null) || { label: status, cls: "text-slate-300 bg-slate-800 border-slate-600" };
  return <span className={`inline-flex rounded-sm border px-2.5 py-1 text-xs font-bold uppercase font-mono ${s.cls}`} data-testid={`dev-status-${status}`}>{s.label}</span>;
}

function DevTimeline({ current }) {
  if (current === "CANCELADA") return <div className="text-red-300 font-mono text-sm border border-red-600 rounded px-3 py-2 bg-red-950/50">DEVOLUÇÃO CANCELADA</div>;
  const idx = DEV_ORDER.indexOf(current);
  return (
    <div className="flex gap-2 overflow-x-auto">
      {DEV_ORDER.map((st, i) => {
        const done = i < idx, active = i === idx;
        return (
          <div key={st} className="flex flex-col items-center min-w-[110px]">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 text-xs font-bold ${active ? "border-primary bg-primary text-primary-foreground" : done ? "border-emerald-500 bg-emerald-500/20 text-emerald-300" : "border-border bg-card text-muted-foreground"}`}>
              {done ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <div className={`text-[9px] mt-1 text-center uppercase font-mono ${active ? "text-primary" : done ? "text-emerald-300" : "text-muted-foreground"}`}>{DEV_STATUS[st].label}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function Devolucoes() {
  const { user } = useAuth();
  const canCD = CD_ROLES.includes(user.role);
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [confOpen, setConfOpen] = useState(false);
  const [destinos, setDestinos] = useState({});

  // create form
  const [units, setUnits] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [busca, setBusca] = useState("");
  const [cart, setCart] = useState({});
  const [motivo, setMotivo] = useState("");
  const [pedidos, setPedidos] = useState([]);
  const [pedidoId, setPedidoId] = useState("");
  const [cancOpen, setCancOpen] = useState(false);
  const [cancMotivo, setCancMotivo] = useState("");
  const podeCancelar = (d) => (["GESTOR", "MASTER", "SUPER_ADMIN", "ADMIN"].includes(user.role) && ["SOLICITADA", "EM_TRANSPORTE", "RECEBIDA_CD", "CONFERIDA"].includes(d.status))
    || (user.role === "UNIDADE" && d.status === "SOLICITADA" && d.itens.every((i) => !(i.qtd_recebida_cd > 0)));

  const load = useCallback(async () => {
    const { data } = await api.get("/devolucoes");
    setList(data);
  }, []);
  const loadDev = async (id) => { const { data } = await api.get(`/devolucoes/${id}`); setSel(data); };

  useEffect(() => {
    load();
    api.get("/units").then((r) => { const uu = r.data.filter((u) => u.tipo === "UNIDADE"); setUnits(uu); if (uu.length) setUnitId(uu[0].id); });
    api.get("/produtos").then((r) => setProdutos(r.data.filter((p) => p.ativo)));
    api.get("/pedidos").then((r) => setPedidos(r.data.filter((p) => ["RECEBIDO", "CONCLUIDO"].includes(p.status)))).catch(() => {});
  }, [load]);
  const pedidosUnit = pedidos.filter((p) => p.unit_id === unitId);
  const pedidoSel = pedidosUnit.find((p) => p.id === pedidoId);

  const flashScreen = (ok) => { setFlash(ok ? "scan-flash-ok" : "scan-flash-err"); setTimeout(() => setFlash(""), 500); };

  const filtered = useMemo(() => produtos.filter((p) => {
    const b = busca.toLowerCase();
    return !b || p.nome.toLowerCase().includes(b) || p.sku.toLowerCase().includes(b) || p.ean.includes(b);
  }).slice(0, 30), [produtos, busca]);

  const addCart = (p) => setCart((c) => ({ ...c, [p.id]: { produto: p, qtd: (c[p.id]?.qtd || 0) + 1 } }));
  const setQ = (id, q) => setCart((c) => q <= 0 ? Object.fromEntries(Object.entries(c).filter(([k]) => k !== id)) : { ...c, [id]: { ...c[id], qtd: q } });
  const cartItems = Object.values(cart);

  const idemRef = useRef(crypto.randomUUID());
  const enviando = useRef(false);
  const criar = async () => {
    if (!unitId || !cartItems.length) return toast.error("Selecione unidade e itens");
    if (enviando.current) return;
    enviando.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/devolucoes", {
        unit_id: unitId, motivo, pedido_origem_id: pedidoSel ? pedidoSel.id : null, idem_key: idemRef.current,
        itens: cartItems.map((i) => ({ sku_id: i.produto.id, quantidade: i.qtd })),
      });
      idemRef.current = crypto.randomUUID();
      toast.success(`Devolução ${data.numero} criada`);
      setCreateOpen(false); setCart({}); setMotivo(""); setPedidoId("");
      await load(); loadDev(data.id);
    } catch (e) {
      if (e.response?.status === 409 && /restituíd|restitu[ií]dos|nova solicita/i.test(e.response?.data?.detail || "")) idemRef.current = crypto.randomUUID();
      toast.error(e.response?.data?.detail || "Erro ao criar");
    }
    finally { setBusy(false); enviando.current = false; }
  };

  const act = async (fn) => { setBusy(true); try { await fn(); await loadDev(sel.id); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Erro"); await loadDev(sel.id).catch(() => {}); await load().catch(() => {}); } finally { setBusy(false); } };

  const bipar = async ({ codigo, quantidade, is_master }) => {
    try {
      const { data } = await api.post(`/devolucoes/${sel.id}/receber/bipar`, { codigo, quantidade, is_master });
      flashScreen(true);
      toast.success(`${data.nome}: ${data.qtd_recebida_cd}/${data.quantidade}`);
      await loadDev(sel.id);
    } catch (e) { flashScreen(false); toast.error(e.response?.data?.detail || "Bipagem inválida"); }
  };

  const openConf = () => { const d = {}; sel.itens.forEach((it) => { d[it.id] = it.destino_final || "RETORNO"; }); setDestinos(d); setConfOpen(true); };
  const salvarConf = async () => {
    await act(async () => { await api.post(`/devolucoes/${sel.id}/conferir`, { destinos }); });
    setConfOpen(false);
  };

  if (sel) {
    const st = sel.status;
    return (
      <div className={`space-y-5 ${flash} rounded-lg`} data-testid="devolucao-detalhe">
        <Button variant="outline" size="sm" onClick={() => setSel(null)}>← Devoluções</Button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase">{sel.numero}</h1>
            <div className="text-sm text-muted-foreground">{sel.itens.length} itens · {fmtDate(sel.created_at)}</div>
            {sel.motivo && <div className="text-sm text-muted-foreground">Motivo: {sel.motivo}</div>}
            {sel.pedido_origem_numero && <div className="text-sm text-muted-foreground" data-testid="dev-pedido-numero">Pedido de origem: {sel.pedido_origem_numero}</div>}
          </div>
          <DevBadge status={st} />
        </div>

        <Card className="bg-card/70 p-4"><DevTimeline current={st} /></Card>
        <Dialog open={cancOpen} onOpenChange={setCancOpen}>
          <DialogContent data-testid="cancelar-dev-dialog">
            <DialogHeader><DialogTitle>Cancelar {sel.numero}</DialogTitle><DialogDescription>O saldo debitado da unidade volta ao estoque dela. Usuário, horário e motivo ficam registrados.</DialogDescription></DialogHeader>
            <Label>Motivo do cancelamento</Label>
            <Textarea rows={3} value={cancMotivo} onChange={(e) => setCancMotivo(e.target.value)} data-testid="cancelar-dev-motivo" />
            {cancMotivo.trim().length > 0 && cancMotivo.trim().length < 5 && <div className="text-xs text-red-300">Mínimo 5 caracteres</div>}
            <DialogFooter><Button variant="outline" onClick={() => setCancOpen(false)}>Voltar</Button>
              <Button variant="destructive" disabled={busy || cancMotivo.trim().length < 5} onClick={() => { setCancOpen(false); act(async () => { await api.post(`/devolucoes/${sel.id}/cancelar`, { motivo: cancMotivo.trim() }); toast.success("Devolução cancelada — saldo devolvido à unidade"); }); }} data-testid="cancelar-dev-confirmar">Confirmar cancelamento</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Card className="bg-card/70 p-4">
          <div className="flex flex-wrap gap-2">
            {st === "SOLICITADA" && <Button onClick={() => act(() => api.post(`/devolucoes/${sel.id}/despachar`))} disabled={busy} data-testid="btn-despachar"><Truck className="w-4 h-4" /> Despachar (Em Transporte)</Button>}
            {st === "EM_TRANSPORTE" && canCD && <Button onClick={() => act(() => api.post(`/devolucoes/${sel.id}/receber/iniciar`))} disabled={busy} data-testid="btn-receber-cd"><PackageCheck className="w-4 h-4" /> Iniciar Recebimento no CD</Button>}
            {st === "RECEBIDA_CD" && canCD && <Button onClick={openConf} disabled={busy} data-testid="btn-conferir-dev"><ClipboardCheck className="w-4 h-4" /> Conferir & Definir Destino</Button>}
            {st === "CONFERIDA" && canCD && <Button onClick={() => act(async () => { const { data } = await api.post(`/devolucoes/${sel.id}/finalizar`); toast.success(data?.status === "EM_ANALISE" ? "Devolução finalizada — avaria enviada para análise" : "Devolução finalizada com sucesso"); })} disabled={busy} data-testid="btn-finalizar-dev"><Check className="w-4 h-4" /> Finalizar Devolução</Button>}
            {podeCancelar(sel) &&
              <Button variant="outline" className="text-red-400 border-red-500/40" onClick={() => { setCancMotivo(""); setCancOpen(true); }} disabled={busy} data-testid="btn-cancelar-dev">Cancelar devolução</Button>}
            {["FINALIZANDO", "CANCELANDO"].includes(st) && <div className="text-amber-300 text-sm" role="status" data-testid="dev-recuperacao-pendente">
              {st === "FINALIZANDO" ? "Finalização" : "Cancelamento"} em recuperação. Aguarde a atualização antes de iniciar outra ação.
              <Button variant="outline" disabled={busy} onClick={() => act(() => Promise.resolve())} data-testid="btn-atualizar-situacao">Atualizar situação</Button>
            </div>}
            {st === "CANCELADA" && sel.cancelamento && <div className="text-sm text-red-300" data-testid="dev-cancelamento-info">Cancelada por {sel.cancelamento.usuario} em {new Date(sel.cancelamento.em).toLocaleString("pt-BR")} — {sel.cancelamento.motivo}</div>}
            {st === "FINALIZADA" && <div className="text-emerald-300 text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Devolução finalizada — {sel.itens.some((i) => i.lote_desconhecido && i.destino_final === "RETORNO") ? "itens sem lote foram para a quarentena do CD; os demais creditados ao estoque" : "retornos creditados ao estoque do CD"}</div>}
          </div>
        </Card>

        {st === "RECEBIDA_CD" && canCD && <FastScanInput label="Bipar Devolução no CD" onScan={bipar} testid="dev-scan" />}

        <Card className="bg-card/70 p-4">
          <div className="font-display text-lg font-bold uppercase mb-3">Itens</div>
          <div className="space-y-2">
            {sel.itens.map((it) => (
              <div key={it.id} className="flex items-center justify-between border-b border-border pb-2" data-testid={`dev-item-${it.sku}`}>
                <div>
                  <div className="text-sm font-medium">{it.nome}</div>
                  <div className="text-[11px] font-mono text-muted-foreground">{it.sku} · EAN {it.ean}</div>
                  {it.lotes_devolucao?.length > 0 && <div className="text-[11px] text-sky-300" data-testid={`dev-lotes-${it.sku}`}>Lotes: {it.lotes_devolucao.map((l) => `${l.lote} (${l.qtd}${l.validade ? ` · val. ${l.validade.split("-").reverse().join("/")}` : ""})`).join(", ")}</div>}
                  {it.lote_desconhecido && st !== "CANCELADA" && (st !== "FINALIZADA" || it.destino_final === "RETORNO") && <div className="text-[11px] text-amber-300" data-testid={`dev-quarentena-${it.sku}`}>{st === "FINALIZADA" ? "Sem lote identificado — enviado para a quarentena do CD (aguarda avaliação)" : "Sem lote identificado — entrará em quarentena"}</div>}
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-center"><div className="text-[9px] uppercase text-muted-foreground">Qtd</div><div className="font-mono font-bold">{it.quantidade}</div></div>
                  <div className="text-center"><div className="text-[9px] uppercase text-muted-foreground">Recebido CD</div><div className="font-mono font-bold text-amber-300">{it.qtd_recebida_cd}</div></div>
                  {it.destino_final && <span className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm font-mono bg-secondary text-foreground" data-testid={`dev-destino-${it.sku}`}>{it.lote_desconhecido && it.destino_final === "RETORNO" ? "Quarentena (sem lote)" : (DESTINOS.find((d) => d.id === it.destino_final)?.label || it.destino_final)}</span>}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Modal conferência destinos */}
        <Dialog open={confOpen} onOpenChange={setConfOpen}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display uppercase">Conferência & Destino Final</DialogTitle>
              <DialogDescription>Defina o destino de cada item recebido no CD.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              {sel.itens.map((it) => (
                <div key={it.id} className="border border-border rounded p-3">
                  <div className="text-sm font-medium">{it.nome}</div>
                  <div className="text-xs text-muted-foreground mb-2 font-mono">Recebido no CD: {it.qtd_recebida_cd}</div>
                  <Select value={destinos[it.id]} onValueChange={(v) => setDestinos((d) => ({ ...d, [it.id]: v }))}>
                    <SelectTrigger data-testid={`dest-${it.sku}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{DESTINOS.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfOpen(false)}>Cancelar</Button>
              <Button onClick={salvarConf} disabled={busy} data-testid="btn-salvar-conferencia-dev">Confirmar Conferência</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="devolucoes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Retorno ao CD</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><Undo2 className="w-8 h-8 text-primary" /> Devoluções</h1>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-nova-devolucao"><Plus className="w-4 h-4" /> Nova Devolução</Button>
      </div>

      {!list ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : list.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">Nenhuma devolução registrada.</Card>
      ) : (
        <div className="grid gap-3">
          {list.map((d) => (
            <Card key={d.id} className="bg-card/70 p-4 flex items-center justify-between cursor-pointer hover:border-primary/50" onClick={() => loadDev(d.id)} data-testid={`dev-card-${d.numero}`}>
              <div>
                <div className="font-mono font-bold text-lg">{d.numero}</div>
                <div className="text-xs text-muted-foreground">{d.itens.length} itens · {fmtDate(d.created_at)}</div>
              </div>
              <DevBadge status={d.status} />
            </Card>
          ))}
        </div>
      )}

      {/* Modal criar */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">Nova Devolução</DialogTitle>
            <DialogDescription>Selecione a unidade de origem e os itens a devolver ao CD.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Unidade de Origem</Label>
                <Select value={unitId} onValueChange={(v) => { setUnitId(v); setPedidoId(""); }}>
                  <SelectTrigger data-testid="dev-unidade"><SelectValue /></SelectTrigger>
                  <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.codigo} — {u.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Motivo</Label>
                <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: avaria, validade, excesso" data-testid="dev-motivo" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Pedido de origem (recomendado — preserva lote, validade e custo; sem pedido o item entra em quarentena)</Label>
              <Select value={pedidoId || "__none"} onValueChange={(v) => { setPedidoId(v === "__none" ? "" : v); setCart({}); }}>
                <SelectTrigger data-testid="dev-pedido-origem"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__none">Sem pedido de origem (quarentena)</SelectItem>
                  {pedidosUnit.map((p) => <SelectItem key={p.id} value={p.id}>{p.numero} · {fmtDate(p.created_at)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto…" className="pl-9" data-testid="dev-busca" />
            </div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {filtered.filter((p) => !pedidoSel || (pedidoSel.itens || []).some((i) => i.sku_id === p.id)).map((p) => (
                <div key={p.id} className="flex items-center justify-between border border-border rounded p-2">
                  <div className="min-w-0"><div className="text-sm truncate">{p.nome}</div><div className="text-[10px] font-mono text-muted-foreground">{p.sku}</div></div>
                  <Button size="sm" variant="outline" onClick={() => addCart(p)} data-testid={`dev-add-${p.sku}`}><Plus className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            {cartItems.length > 0 && (
              <div className="border-t border-border pt-2 space-y-2">
                <div className="text-xs uppercase text-muted-foreground">Itens a devolver</div>
                {cartItems.map(({ produto: p, qtd }) => (
                  <div key={p.id} className="flex items-center gap-2">
                    <div className="flex-1 text-sm truncate">{p.nome}</div>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setQ(p.id, qtd - 1)}><Minus className="w-3 h-3" /></Button>
                    <Input value={qtd} onChange={(e) => setQ(p.id, Number(e.target.value) || 0)} className="w-16 h-8 text-center font-mono" data-testid={`dev-qtd-${p.sku}`} />
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setQ(p.id, qtd + 1)}><Plus className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => setQ(p.id, 0)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={criar} disabled={busy || !cartItems.length} data-testid="btn-criar-devolucao">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar Devolução"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

